import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import { ENHANCED_RECURRING_TRANSACTION_DETAIL_CONFIG } from "./RecurringTransactionDetailConfig";
import {
  textField,
  selectField,
  numberField,
  dateField,
  fkField,
  STANDARD_TABLE_FEATURES,
} from "@/features/crm-table-config/table-config-builders";
import { getRealmSelectOptions } from "@/lib/integrations/quickbooks/realms";
import { COMMON_QUICK_FILTERS } from "@/components/shared/config/quick-filters/common/common-quick-filters";

/**
 * Progressive-disclosure section layout for the Create Recurring Transaction form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_RECURRING_TRANSACTION_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["quickbooks_realm_id", "customer_id", "item", "amount", "billing_cadence", "next_bill_on"],
  },
  {
    id: "relationships",
    kind: "collapsible",
    summary: "Link to a company",
    fieldNames: ["company_id"],
  },
  {
    id: "description",
    kind: "collapsible",
    title: "Quantity & description",
    fieldNames: ["quantity", "description"],
  },
];
import {
  DollarSign,
  Package,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Hash,
  Building2,
} from "lucide-react";

export const RECURRING_TRANSACTIONS_CONFIG: SimpleTableConfig<RecurringTransaction> = {
  entity: "recurring-transactions",
  displayName: "Recurring Transactions",
  statusFieldId: "sync_status",
  defaultPageSize: 10,
  defaultSort: { field: "created_at", direction: "desc" },

  // Quick filters (pill bar): filter by QuickBooks billing company (WTC vs SM)
  quickFilters: [COMMON_QUICK_FILTERS.realm],

  // Shared actions from detail config
  detailActions: ENHANCED_RECURRING_TRANSACTION_DETAIL_CONFIG.actions,

  createFormSections: CREATE_RECURRING_TRANSACTION_FORM_SECTIONS,

  // Grouped detail-section layout (shared inline edit panel + mobile sectioned detail)
  mobileDetailSections: [
    { id: "transaction", label: "Transaction Details" },
    { id: "customer", label: "Customer & Company" },
    { id: "quickbooks", label: "QuickBooks Sync" },
    { id: "notes", label: "Description" },
  ],
  mobileDetailUnsectionedLabel: "Other",

  /** Default table column order: billing company → item → customer → amount → qty → sync */
  tableColumnOrder: [
    "quickbooks_realm_id",
    "item",
    "customer_id",
    "amount",
    "billing_cadence",
    "next_bill_on",
    "quantity",
    "sync_status",
    "last_synced_at",
  ],

  fields: {
    customer_id: fkField("client-select", {
      label: "Customer",
      foreignKey: {
        referencedTable: "contacts",
        referencedColumn: "id",
        constraintName: "recurring_transactions_customer_id_fkey",
        onDelete: "CASCADE",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      // Optional (Option A): customer_id is nullable — a company-only recurring
      // transaction has no contact. Billing derives the QuickBooks customer from
      // the company, so a contact is not required to create/bill the row.
      required: false,
      placeholder: "Select customer",
      icon: "User",
      mobileDetailSectionId: "customer",
      allowedRoles: ["wtc_admin"],
    }),
    company_id: fkField("select", {
      label: "Company",
      foreignKey: {
        referencedTable: "companies",
        referencedColumn: "id",
        constraintName: "recurring_transactions_company_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "company-select",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "Select company",
      icon: "Building2",
      groupable: true,
      mobileDetailSectionId: "customer",
      allowedRoles: ["wtc_admin"],
    }),
    quickbooks_realm_id: selectField({
      label: "Billing Company",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 170,
      minWidth: 140,
      maxWidth: 220,
      required: true,
      validation: {
        required: "Billing company is required.",
      },
      options: getRealmSelectOptions(),
      placeholder: "Select billing company",
      icon: "Building2",
      groupable: true,
      mobileDetailSectionId: "quickbooks",
      allowedRoles: ["wtc_admin"],
    }),
    item: textField({
      label: "Item",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "contains",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      required: true,
      validation: {
        required: "Item is required.",
      },
      placeholder: "e.g., Website Hosting, Consulting",
      icon: "Package",
      mobileDetailSectionId: "transaction",
      allowedRoles: ["wtc_admin"],
    }),
    description: {
      type: "textarea",
      label: "Description",
      sortable: false,
      filterable: false,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      placeholder: "Optional description or notes",
      mobileDetailSectionId: "notes",
      allowedRoles: ["wtc_admin"],
    },
    amount: numberField({
      label: "Amount (per billing period)",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "range",
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      required: true,
      validation: {
        required: "Amount is required.",
        min: 0,
      },
      placeholder: "0.00",
      icon: "DollarSign",
      mobileDetailSectionId: "transaction",
      allowedRoles: ["wtc_admin"],
    }),
    billing_cadence: selectField({
      label: "Billing Cadence",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 140,
      minWidth: 110,
      maxWidth: 170,
      options: [
        { label: "Monthly", value: "monthly" },
        { label: "Quarterly", value: "quarterly" },
        { label: "Annual", value: "annual" },
      ],
      placeholder: "Monthly",
      icon: "Calendar",
      groupable: true,
      mobileDetailSectionId: "transaction",
      allowedRoles: ["wtc_admin"],
    }),
    next_bill_on: dateField({
      label: "Next Bill Date",
      sortable: true,
      filterable: false,
      resizable: true,
      hideable: true,
      editable: true,
      width: 140,
      minWidth: 110,
      maxWidth: 180,
      // Required for quarterly/annual only (cross-field): enforced on save by
      // validateCadenceAnchorInput in the create/update hooks, and by a DB CHECK.
      placeholder: "Quarterly / annual only",
      icon: "Calendar",
      mobileDetailSectionId: "transaction",
      allowedRoles: ["wtc_admin"],
    }),
    quantity: numberField({
      label: "Quantity",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "range",
      width: 100,
      minWidth: 80,
      maxWidth: 120,
      validation: {
        min: 0.01,
      },
      placeholder: "1",
      icon: "Package",
      mobileDetailSectionId: "transaction",
      allowedRoles: ["wtc_admin"],
    }),
    sync_status: selectField({
      label: "Sync Status",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false, // Read-only
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 150,
      options: [
        { label: "Pending", value: "pending" },
        { label: "Synced", value: "synced" },
        { label: "Failed", value: "failed" },
      ],
      icon: "CheckCircle",
      groupable: true,
      mobileDetailSectionId: "quickbooks",
      allowedRoles: ["wtc_admin"],
    }),
    last_synced_at: dateField({
      label: "Last Synced",
      sortable: true,
      filterable: false,
      resizable: true,
      hideable: true,
      editable: false, // Read-only
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "Clock",
      mobileDetailSectionId: "quickbooks",
      allowedRoles: ["wtc_admin"],
    }),
    last_synced_month: textField({
      label: "Last Synced Month",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false, // Read-only
      filterType: "exact",
      width: 150,
      minWidth: 120,
      maxWidth: 180,
      placeholder: "YYYY-MM",
      icon: "Calendar",
      mobileDetailSectionId: "quickbooks",
      allowedRoles: ["wtc_admin"],
    }),
    quickbooks_id: textField({
      label: "QuickBooks ID",
      sortable: false,
      filterable: false,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false, // Read-only
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      placeholder: "Not synced",
      icon: "Hash",
      mobileDetailSectionId: "quickbooks",
      allowedRoles: ["wtc_admin"],
    }),
  },

  features: { ...STANDARD_TABLE_FEATURES, infiniteScroll: true, calendarView: true },

  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },

  // Custom filter hooks for saved views (can be populated later if needed)
  customFilterHooks: {},
};
